import { Component, Element, Host, Listen, Prop, h } from '@stencil/core';
import type { AnchorInterface } from '../../../utils/elements-interface';
import { generateAnchorAttributes, openURL } from '../../../utils/elements-interface';

/**
 * @slot  - Designed to add content inside the list item component.
 */

@Component({
  tag: 'sy-list-item',
  styleUrl: 'scss/index.scss',
  shadow: true,
})
export class ListItem implements AnchorInterface {
  @Element() el: HTMLSyListItemElement;

  private anchorEl: HTMLAnchorElement | undefined;

  /**
   HTML <a> download attribute.
   */
  @Prop() download: string | undefined;

  /**
   * HTML <a> href attribute.
   */
  @Prop({ reflect: true }) href: string | undefined;

  /**
   * HTML <a> rel attribute.
   */
  @Prop() rel: string | undefined;

  /**
   * HTML <a> target attribute.
   */
  @Prop() target: '_blank' | '_self' | '_parent' | '_top';

  /**
   * Indicate if the item is disabled.
   */
  @Prop({ reflect: true }) disabled: boolean;

  /**
   * Indicate if the item is active.
   */
  @Prop({ reflect: true }) active: boolean;

  private getClasses() {
    return {
      class: `sy-item-inner-content `,
    };
  }

  private handleClick = (event: Event) => {
    if (this.href) {
      openURL(this.href, event);
    }
  };

  /**
   * Activates the action button with the enter key.
   *
   * @param {KeyboardEvent} event
   */
  @Listen('keydown', { passive: false })
  onKeydown(event: KeyboardEvent): void {
    // The action button is activated by space on the keyup event, but the
    // default action for space is already triggered on keydown. It needs to be
    // prevented to stop scrolling the page before activating the button.
    if (event.key === ' ') {
      event.preventDefault();
    } else if (event.key === 'Enter') {
      // If enter is pressed, activate the button
      event.preventDefault();
      this.anchorEl ? this.anchorEl.click() : this.el.click();
    }
  }

  /**
   * Activates the action button with the space key.
   *
   * @param {KeyboardEvent} event
   */
  @Listen('keyup', { passive: false })
  onKeyup(event: KeyboardEvent): void {
    if (event.key === ' ') {
      event.preventDefault();
      this.anchorEl ? this.anchorEl.click() : this.el.click();
    }
  }

  render() {
    return (
      <Host
        tabIndex={this.disabled ? null : 0}
        role={this.href ? 'link' : 'button'}
        aria-disabled={this.disabled ? 'true' : null}
        {...(this.href ? {} : { 'aria-pressed': this.active ? 'true' : 'false' })}
      >
        {this.href ? (
          <a
            ref={(el) => (this.anchorEl = el)}
            onClick={this.handleClick}
            tabIndex={-1}
            {...this.getClasses()}
            {...generateAnchorAttributes(this)}
          >
            <slot></slot>
          </a>
        ) : (
          <slot></slot>
        )}
      </Host>
    );
  }
}
